// src/utils/dimensions.js
let dimensionsCache = new Map();

export const clearDimensionsCache = () => {
  dimensionsCache.clear();
};

/**
 * Calculates dimensions (widthInches, heightInches) for a Fabric.js i-text object.
 * @param {Object} obj - Fabric.js i-text object
 * @param {string} fontCode - Font code
 * @param {string} sizeCode - Size code
 * @param {Array} fonts - Array of font objects
 * @param {Object} [options] - Optional overrides
 * @returns {Object} { widthInches, heightInches }
 */
export const setDimensions = (obj, fontCode, sizeCode, fonts, options = {}) => {

  try {
    if (!obj || !obj.text?.trim()) {
      return { widthInches: 0, heightInches: 0 };
    }

    const text = obj.text.trim();
    const scaleX = Math.min(obj.scaleX || 1, 1);
    const fontSize = obj.fontSize || 100;

    // Find font
    const font = fonts.find(f => f.code === fontCode) || {
      code: fontCode,
      name: fontCode,
      fontAdjustment: 1.0,
      charWidths: {},
    };

    // Size height
    const expectedHeights = { "02": 2, "03": 3, "04": 4, "06": 6, "08": 8, "10": 10, "12": 12 };
    const sizeObj = font.sizes?.find(s => s.code === sizeCode);
    const heightInches = options.heightOverride || sizeObj?.height || expectedHeights[sizeCode] || 6;

    let widthInches = 0;
    const precision = options.precision || "precise";

    if (precision === "precise" && font.charWidths && Object.keys(font.charWidths).length > 0) {
      let total = 0;
      for (const char of text) {
        total += font.charWidths[char] || 1.0;
      }
      widthInches = total * heightInches * (font.fontAdjustment || 1.0) * scaleX;
    } else {
      const avgCharWidth = 0.6;
      const baseHeight = 2;
      const heightScale = heightInches / baseHeight;
      widthInches = text.length * avgCharWidth * heightInches * scaleX * (font.fontAdjustment || 1.0) * heightScale;
    }

    const result = {
      widthInches: Number(widthInches.toFixed(2)),
      heightInches: Number(heightInches.toFixed(2)),
    };

    // Cache
    const cacheKey = JSON.stringify({ text, fontCode, sizeCode, scaleX, precision });
    dimensionsCache.set(cacheKey, result);

    return result;
  } catch (error) {
    console.error("[Dimensions] ERROR", error);
    return { widthInches: 0, heightInches: 0 };
  }
};
